import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import Seo, { LOCAL_BUSINESS_LD, breadcrumbLd } from '../components/Seo';
import { MaskedLines, FadeUp, Eyebrow } from '../components/motion';
import QuoteForm from '../components/QuoteForm';
import Faq from '../components/sections/Faq';
import NotFound from './NotFound';
import { useT } from '../i18n/LangContext';
import { useContent } from '../data/useContent';
import { TILES, SERVICE_FAQ_LD } from '../data/content';

export default function ServicePage() {
  const { slug } = useParams();
  const t = useT();
  const { SERVICES } = useContent();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <NotFound />;

  const gallery = [service.img, ...TILES.filter((tile) => tile.img !== service.img).slice(0, 3).map((tile) => tile.img)];
  const serviceLd = {
    '@context': 'https://schema.org', '@type': 'Service', name: service.name,
    provider: LOCAL_BUSINESS_LD, areaServed: 'Ottawa, ON',
    url: `${process.env.REACT_APP_BACKEND_URL}/${service.slug}`, description: service.metaDesc,
  };

  return (
    <>
      <Seo title={service.metaTitle} description={service.metaDesc}
        jsonLd={[serviceLd, SERVICE_FAQ_LD(service), breadcrumbLd(['Home', service.name])]} />

      <section className="relative overflow-hidden bg-ink">
        <img src={service.img} alt={`${service.name} — Reverence Services, Ottawa`} className="photo-treat absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
        <div className="container-site relative z-10 py-24 md:py-32">
          <Eyebrow>{t['sp.eyebrow']}</Eyebrow>
          <MaskedLines as="h1" className="mt-4 font-serif text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
            lines={[{ text: service.name, cls: 'text-white' }, { text: t['sp.inOttawa'], cls: 'italic text-gold' }]} />
          <FadeUp delay={0.3}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 md:text-lg">{service.tagline}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link data-testid="service-hero-cta" to={`/quote?service=${encodeURIComponent(service.name)}`} className="gold-btn">
                {t['sp.cta']} <ArrowRight className="h-4 w-4" />
              </Link>
              <a data-testid="service-hero-call" href="tel:+16138595893" className="ghost-btn">613-859-5893</a>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-white py-24 text-ink md:py-32">
        <div className="container-site grid gap-14 lg:grid-cols-2">
          <FadeUp>
            <Eyebrow dark>{t['sp.included']}</Eyebrow>
            <h2 className="mt-4 font-serif text-4xl font-semibold leading-tight tracking-tight">{t['sp.includedH2a']} <span className="italic text-gold-dim">{t['sp.includedH2b']}</span></h2>
            <ul className="mt-8 space-y-4">
              {service.included.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base font-semibold">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-gold-dim" /> {item}
                </li>
              ))}
            </ul>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Eyebrow dark>{t['sp.process']}</Eyebrow>
            <ol className="mt-8 space-y-8">
              {service.process.map((p, i) => (
                <li key={p.t} data-testid={`process-step-${i}`} className="flex gap-6">
                  <span className="font-serif text-4xl font-semibold text-gold/40">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="font-serif text-xl font-semibold">{p.t}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-neutral-500">{p.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </FadeUp>
        </div>
      </section>

      <section className="bg-ink py-24">
        <div className="container-site">
          <FadeUp className="flex items-end justify-between">
            <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl">
              {t['sp.recentA']} {service.name.toLowerCase()} {t['sp.recentB']}
            </h2>
            <Link data-testid="service-gallery-link" to="/gallery" className="hidden items-center gap-2 text-sm font-bold text-gold hover:text-gold-light sm:flex">
              {t['sp.fullGallery']} <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
          <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {gallery.map((img, i) => (
              <FadeUp key={i} delay={i * 0.06}>
                <img src={img} alt={`${service.name} — projet ${i + 1} à Ottawa`} loading="lazy"
                  className="photo-treat aspect-[4/3] w-full rounded-xl object-cover shadow-lg transition-transform duration-500 hover:-translate-y-1" />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <Faq items={service.faqs} chapterN={9} dark={false} title={`${service.name} — FAQ`} idPrefix={`${service.slug}-faq`} />

      <section className="bg-ink py-24 md:py-32">
        <div className="container-site grid items-center gap-12 lg:grid-cols-2">
          <FadeUp>
            <Eyebrow>{t['sp.quoteEyebrow']}</Eyebrow>
            <h2 className="mt-4 font-serif text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
              {t['sp.quoteH2a']} {service.name.toLowerCase()} <span className="italic text-gold">{t['sp.quoteH2b']}</span>
            </h2>
            <p className="mt-5 max-w-md text-base text-silver">{t['sp.quoteP']}</p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="rounded-2xl bg-white p-7 text-ink shadow-2xl md:p-9">
              <QuoteForm variant="inline" source={`service-${service.slug}`} idPrefix={`${service.slug}-quote`} />
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
